import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { getAIDashboards, updateAIDashboard } from "../queries/ai.graphql";
import { useWorkspaceFeatureFlags } from "../contexts/global/WorkspaceContext";
import decodeHTMLEntities from "../util/decodeHTMLEntities";

/**
 * Hook to extract all AI-created dashboards from chat history
 * @returns {Record<string, any>} { dashboards: Array, loading: boolean, error: Error, selectedDashboardId: string }
 */
const useAIDashboards = () => {
  const { enableAIChat } = useWorkspaceFeatureFlags();

  // Dashboards are listed when AI chat is enabled.
  const {
    data: dashboardsData,
    loading,
    error,
  } = useQuery(getAIDashboards, {
    fetchPolicy: "cache-and-network",
    skip: !enableAIChat,
  });

  // Hiding drops the dashboard from the list, so the list has to be re-read
  // rather than patched; renames ride along on the same refetch.
  const [updateDashboard] = useMutation(updateAIDashboard, {
    refetchQueries: [getAIDashboards],
    awaitRefetchQueries: true,
  });

  const renameDashboard = useCallback(
    (id, title) => updateDashboard({ variables: { input: { id, title } } }),
    [updateDashboard]
  );

  /** Soft delete: the dashboard stays in the database but the user stops seeing it. */
  const hideDashboard = useCallback(
    (id) => updateDashboard({ variables: { input: { id, isHidden: true } } }),
    [updateDashboard]
  );

  const dashboards = useMemo(() => {
    if (!dashboardsData?.dashboards?.length) {
      return [];
    }

    const visible = dashboardsData.dashboards;

    // Convert map to array and sort by creation date (most recent first)
    return visible
      .map((dashboard) => ({
        ...dashboard,
        title: decodeHTMLEntities(dashboard.title),
        createdAt: new Date(parseInt(dashboard.createdAt, 10)),
        updatedAt: new Date(parseInt(dashboard.updatedAt, 10)),
      }))
      .sort((a, b) => b.createdAt - a.createdAt)
      .map(({ messageIndex, ...dashboard }) => dashboard); // Remove internal fields from final output
  }, [dashboardsData]);

  return {
    dashboards,
    loading,
    error,
    renameDashboard,
    hideDashboard,
  };
};

export default useAIDashboards;
